import React, { useState, useEffect } from 'react';
import CelebrationsPopup from '../components/CelebrationsPopup';

const PresentersDashboard = () => {
  // Language state
  const [language, setLanguage] = useState('en');
  const [activeSection, setActiveSection] = useState('celebrations');
  const [weatherData, setWeatherData] = useState({});
  const [programSchedule, setProgramSchedule] = useState([]);
  const [presenters, setPresenters] = useState({});
  const [liveBroadcastSchedule, setLiveBroadcastSchedule] = useState({});
  const [submittedTestimonies, setSubmittedTestimonies] = useState([]);
  const [submittedCalls, setSubmittedCalls] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Celebrations state
  const [celebrations, setCelebrations] = useState([]);
  const [celebrationsLoading, setCelebrationsLoading] = useState(false);
  const [celebrationLanguage, setCelebrationLanguage] = useState('en');
  
  // Now On Air state
  const [nowOnAir, setNowOnAir] = useState(null);
  const [nowOnAirLoading, setNowOnAirLoading] = useState(false);
  
  // WhatsApp messages state
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappUnreadCount, setWhatsappUnreadCount] = useState(0);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarMessages, setCalendarMessages] = useState([]);

  // Form states
  const [testimonyForm, setTestimonyForm] = useState({
    date: new Date().toISOString().split('T')[0],
    name: '',
    location: '',
    program: '',
    summary: ''
  });

  const [callForm, setCallForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
    phone: '',
    summary: '',
    category: 'Testimony'
  });

  // Load language preference from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('kioo-dashboard-language') || 'en';
    setLanguage(savedLanguage);
    
    // Load submitted data from localStorage
    const savedTestimonies = JSON.parse(localStorage.getItem('kioo-testimonies') || '[]');
    const savedCalls = JSON.parse(localStorage.getItem('kioo-calls') || '[]');
    const savedNotifications = JSON.parse(localStorage.getItem('kioo-notifications') || '[]');
    
    setSubmittedTestimonies(savedTestimonies);
    setSubmittedCalls(savedCalls);
    setNotifications(savedNotifications);
    setUnreadCount(savedNotifications.filter(n => !n.read).length);
    
    // Request permission for browser notifications
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Notification functions
  const addNotification = (type, message, details) => {
    const notification = {
      id: Date.now().toString(),
      type, // 'testimony' or 'call'
      message,
      details,
      timestamp: new Date().toISOString(),
      read: false
    };
    
    const updatedNotifications = [notification, ...notifications];
    setNotifications(updatedNotifications);
    setUnreadCount(prev => prev + 1);
    localStorage.setItem('kioo-notifications', JSON.stringify(updatedNotifications));
    
    // Show browser notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Kioo Radio Dashboard', {
        body: message,
        icon: '/favicon.ico',
        badge: '/favicon.ico'
      });
    }
    
    // Show toast notification
    showToast(message, type);
  };

  const showToast = (message, type) => {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `fixed top-20 right-4 z-50 max-w-sm p-4 rounded-lg shadow-lg text-white transform transition-transform duration-300 translate-x-full ${
      type === 'testimony' ? 'bg-green-600' : 'bg-blue-600'
    }`;
    toast.innerHTML = `
      <div class="flex items-center">
        <div class="mr-3 text-xl">${type === 'testimony' ? '📝' : '📞'}</div>
        <div class="flex-1">
          <div class="font-medium">New ${type === 'testimony' ? 'Testimony' : 'Call Log'}</div>
          <div class="text-sm opacity-90">${message}</div>
        </div>
        <button class="ml-3 text-white hover:text-gray-200" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    // Animate in
    setTimeout(() => toast.classList.remove('translate-x-full'), 100);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  };

  const markNotificationsAsRead = () => {
    const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updatedNotifications);
    setUnreadCount(0);
    localStorage.setItem('kioo-notifications', JSON.stringify(updatedNotifications));
  };

  // Save language preference
  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'fr' : 'en';
    setLanguage(newLanguage);
    localStorage.setItem('kioo-dashboard-language', newLanguage);
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        
        // Fetch weather data
        const weatherRes = await fetch(`${backendUrl}/api/dashboard/weather`);
        if (weatherRes.ok) {
          const weather = await weatherRes.json();
          setWeatherData(weather);
        }

        // Fetch program schedule
        const scheduleRes = await fetch(`${backendUrl}/api/dashboard/schedule`);
        if (scheduleRes.ok) {
          const schedule = await scheduleRes.json();
          setProgramSchedule(schedule);
        }

        // Fetch presenters
        const presentersRes = await fetch(`${backendUrl}/api/dashboard/presenters`);
        if (presentersRes.ok) {
          const presenterData = await presentersRes.json();
          setPresenters(presenterData);
        }

        // Fetch live broadcast schedule
        const liveScheduleRes = await fetch(`${backendUrl}/api/live-broadcast-schedule`);
        if (liveScheduleRes.ok) {
          const liveScheduleData = await liveScheduleRes.json();
          setLiveBroadcastSchedule(liveScheduleData);
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchDashboardData();
    
    // Set up weather refresh every 15 minutes (900000ms)
    const weatherInterval = setInterval(() => {
      const fetchWeatherOnly = async () => {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
          const weatherRes = await fetch(`${backendUrl}/api/dashboard/weather`);
          if (weatherRes.ok) {
            const weather = await weatherRes.json();
            setWeatherData(weather);
            console.log('Weather data refreshed:', new Date().toLocaleTimeString());
          }
        } catch (error) {
          console.error('Error refreshing weather data:', error);
        }
      };
      fetchWeatherOnly();
    }, 900000); // 15 minutes

    // Cleanup interval on component unmount
    return () => clearInterval(weatherInterval);
  }, []);
  
  // Fetch today's celebrations
  const fetchCelebrations = async () => {
    try {
      setCelebrationsLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/celebrations/today`);
      
      if (response.ok) {
        const data = await response.json();
        setCelebrations(data.celebrations || []);
      }
    } catch (error) {
      console.error('Error fetching celebrations:', error);
    } finally {
      setCelebrationsLoading(false);
    }
  };
  
  // Mark celebration as displayed
  const markCelebrationDisplayed = async (greetingId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/celebrations/${greetingId}/mark-displayed`, {
        method: 'POST'
      });
      
      // Update local state
      setCelebrations(celebrations.map(c => 
        c.id === greetingId ? { ...c, displayed: true } : c
      ));
    } catch (error) {
      console.error('Error marking celebration as displayed:', error);
    }
  };
  
  // Copy celebration to clipboard for WhatsApp
  const copyToWhatsApp = (celebration) => {
    const greeting = celebrationLanguage === 'en' 
      ? celebration.greeting_english 
      : celebration.greeting_french;
    
    const emoji = celebration.celebration_type === 'birthday' ? '🎂🎉' : '💑✨';
    const textToCopy = `${emoji} ${greeting}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(textToCopy).then(() => {
      alert(language === 'fr' 
        ? '✅ Copié! Collez maintenant dans WhatsApp.' 
        : '✅ Copied! Now paste in WhatsApp.');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert(language === 'fr' 
        ? '❌ Erreur de copie. Sélectionnez et copiez manuellement.' 
        : '❌ Copy failed. Please select and copy manually.');
    });
  };
  
  // Fetch now on air data
  const fetchNowOnAir = async () => {
    try {
      setNowOnAirLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/now-on-air`);
      
      if (response.ok) {
        const data = await response.json();
        setNowOnAir(data);
      }
    } catch (error) {
      console.error('Error fetching now on air:', error);
    } finally {
      setNowOnAirLoading(false);
    }
  };
  
  // Fetch WhatsApp unread count
  const fetchWhatsAppUnreadCount = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/whatsapp/unread-count`);
      
      if (response.ok) {
        const data = await response.json();
        setWhatsappUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp unread count:', error);
    }
  };
  
  // Fetch WhatsApp messages
  const fetchWhatsAppMessages = async () => {
    try {
      setWhatsappLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/whatsapp/messages?limit=20`);
      
      if (response.ok) {
        const data = await response.json();
        setWhatsappMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp messages:', error);
    } finally {
      setWhatsappLoading(false);
    }
  };
  
  // Fetch messages by date
  const fetchMessagesByDate = async (date) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/whatsapp/messages/date/${date}`);
      
      if (response.ok) {
        const data = await response.json();
        setCalendarMessages(data.messages || []);
        setSelectedDate(date);
      }
    } catch (error) {
      console.error('Error fetching messages by date:', error);
    }
  };
  
  // Fetch WhatsApp unread count on mount and periodically
  useEffect(() => {
    fetchWhatsAppUnreadCount();
    const unreadInterval = setInterval(fetchWhatsAppUnreadCount, 30000); // Every 30 seconds
    
    return () => clearInterval(unreadInterval);
  }, []);
  
  // Fetch celebrations when switching to celebrations tab
  useEffect(() => {
    if (activeSection === 'celebrations') {
      fetchCelebrations();
      fetchNowOnAir();
      
      // Update "Now On Air" every minute
      const nowOnAirInterval = setInterval(fetchNowOnAir, 60000);
      
      return () => {
        clearInterval(nowOnAirInterval);
      };
    }
  }, [activeSection]);

  // Translations
  const t = {
    en: {
      title: 'Kioo Radio Presenters Dashboard',
      language: 'Language',
      weather: 'Current Weather',
      presenters: 'Presenters by Country',
      submissions: 'Recent Submissions',
      testimony: 'Submit Testimony',
      calls: 'Log a Phone Call',
      export: 'Download as Excel',
      social: 'WhatsApp & Facebook',
      
      // Weather
      temperature: 'Temperature',
      condition: 'Condition',
      updated: 'Updated',
      
      // Submissions
      testimonies: 'Recent Testimonies',
      callLogs: 'Recent Phone Calls',
      noSubmissions: 'No submissions yet',
      submittedOn: 'Submitted on',
      
      // Forms
      date: 'Date',
      name: 'Name (Optional)',
      location: 'Location',
      programName: 'Program Name',
      summary: 'Summary',
      phoneNumber: 'Phone Number (Optional)',
      category: 'Category',
      submit: 'Submit',
      
      // Categories
      categories: {
        'Testimony': 'Testimony',
        'Question': 'Question',
        'Complaint': 'Complaint',
        'Prayer Request': 'Prayer Request'
      },
      
      // Success messages
      testimonySuccess: 'Testimony logged successfully!',
      callSuccess: 'Phone call logged successfully!',
      
      // Export
      exportData: 'Export Data',
      noData: 'No data to export',
      
      // Notifications
      notifications: 'Notifications',
      newSubmission: 'New Submission',
      noNotifications: 'No notifications',
      markAllRead: 'Mark all as read',
      
      // Celebrations
      celebrations: 'Live',
      noCelebrations: 'No celebrations today',
      birthdayCelebration: 'Birthday',
      anniversaryCelebration: 'Anniversary',
      markAsRead: 'Mark as Read',
      showEnglish: 'Show English',
      showFrench: 'Show French',
      greetingFor: 'Greeting for',
      location: 'Location',
      celebrationsLoading: 'Loading celebrations...',
      readOnAir: 'Read this greeting on-air to celebrate with our listener family!',
      
      // Now On Air
      nowOnAir: 'Now On Air',
      currentProgram: 'Current Program',
      nextProgram: 'Coming Up Next',
      host: 'Host',
      duration: 'Duration',
      endTime: 'Ends at',
      startTime: 'Starts at',
      minutes: 'minutes',
      noCurrentProgram: 'No program scheduled at this time',
      live: 'LIVE',
      currentTime: 'Current Time'
    },
    fr: {
      title: 'Tableau de Bord des Présentateurs Kioo Radio',
      language: 'Langue',
      weather: 'Météo Actuelle',
      presenters: 'Présentateurs par Pays',
      submissions: 'Soumissions Récentes',
      testimony: 'Soumettre un Témoignage',
      calls: 'Journal d\'Appel',
      export: 'Télécharger en Excel',
      social: 'WhatsApp et Facebook',
      
      // Weather
      temperature: 'Température',
      condition: 'Condition',
      updated: 'Mis à jour',
      
      // Submissions
      testimonies: 'Témoignages Récents',
      callLogs: 'Appels Téléphoniques Récents',
      noSubmissions: 'Aucune soumission pour le moment',
      submittedOn: 'Soumis le',
      
      // Forms
      date: 'Date',
      name: 'Nom (Optionnel)',
      location: 'Lieu',
      programName: 'Nom du Programme',
      summary: 'Résumé',
      phoneNumber: 'Numéro de Téléphone (Optionnel)',
      category: 'Catégorie',
      submit: 'Soumettre',
      
      // Categories
      categories: {
        'Testimony': 'Témoignage',
        'Question': 'Question',
        'Complaint': 'Plainte',
        'Prayer Request': 'Demande de Prière'
      },
      
      // Success messages
      testimonySuccess: 'Témoignage enregistré avec succès!',
      callSuccess: 'Appel téléphonique enregistré avec succès!',
      
      // Export
      exportData: 'Exporter les Données',
      noData: 'Aucune donnée à exporter',
      
      // Notifications
      notifications: 'Notifications',
      newSubmission: 'Nouvelle Soumission',
      noNotifications: 'Aucune notification',
      markAllRead: 'Marquer tout comme lu',
      
      // Celebrations
      celebrations: 'En Direct',
      noCelebrations: 'Aucune célébration aujourd\'hui',
      birthdayCelebration: 'Anniversaire',
      anniversaryCelebration: 'Anniversaire de Mariage',
      markAsRead: 'Marquer comme Lu',
      showEnglish: 'Afficher en Anglais',
      showFrench: 'Afficher en Français',
      greetingFor: 'Salutation pour',
      location: 'Emplacement',
      celebrationsLoading: 'Chargement des célébrations...',
      readOnAir: 'Lisez cette salutation à l\'antenne pour célébrer avec notre famille d\'auditeurs!',
      
      // Now On Air
      nowOnAir: 'En Direct Maintenant',
      currentProgram: 'Programme Actuel',
      nextProgram: 'Prochainement',
      host: 'Animateur',
      duration: 'Durée',
      endTime: 'Fin à',
      startTime: 'Début à',
      minutes: 'minutes',
      noCurrentProgram: 'Aucun programme prévu pour le moment',
      live: 'EN DIRECT',
      currentTime: 'Heure Actuelle'
    }
  };

  // Handle form submissions
  const handleTestimonySubmit = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      // Create testimony with timestamp
      const testimonyWithId = {
        ...testimonyForm,
        id: Date.now().toString(),
        submittedAt: new Date().toISOString()
      };
      
      const response = await fetch(`${backendUrl}/api/dashboard/testimony`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testimonyWithId)
      });

      if (response.ok) {
        // Add to local state and localStorage
        const updatedTestimonies = [testimonyWithId, ...submittedTestimonies];
        setSubmittedTestimonies(updatedTestimonies);
        localStorage.setItem('kioo-testimonies', JSON.stringify(updatedTestimonies));
        
        // Add notification
        const message = `New testimony from ${testimonyWithId.name || 'Anonymous'} in ${testimonyWithId.location}`;
        addNotification('testimony', message, testimonyWithId);
        
        alert(t[language].testimonySuccess);
        setTestimonyForm({
          date: new Date().toISOString().split('T')[0],
          name: '',
          location: '',
          program: '',
          summary: ''
        });
      }
    } catch (error) {
      console.error('Error submitting testimony:', error);
    }
  };

  const handleCallSubmit = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      // Create call log with timestamp
      const callWithId = {
        ...callForm,
        id: Date.now().toString(),
        submittedAt: new Date().toISOString()
      };
      
      const response = await fetch(`${backendUrl}/api/dashboard/call-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(callWithId)
      });

      if (response.ok) {
        // Add to local state and localStorage
        const updatedCalls = [callWithId, ...submittedCalls];
        setSubmittedCalls(updatedCalls);
        localStorage.setItem('kioo-calls', JSON.stringify(updatedCalls));
        
        // Add notification
        const message = `New ${callWithId.category.toLowerCase()} call logged at ${callWithId.time}`;
        addNotification('call', message, callWithId);
        
        alert(t[language].callSuccess);
        setCallForm({
          date: new Date().toISOString().split('T')[0],
          time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
          phone: '',
          summary: '',
          category: 'Testimony'
        });
      }
    } catch (error) {
      console.error('Error submitting call log:', error);
    }
  };

  // Export data functionality
  const exportData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/dashboard/export`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `kioo-radio-dashboard-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl text-gray-600">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Birthday/anniversary popup — surfaces today's CRM celebrations so
          the presenter can read them on air. Auto-shows when there are
          unread greetings; floating bell stays visible thereafter. */}
      <CelebrationsPopup language={language} />

      {/* Header */}
      <div className="bg-green-600 text-white p-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">{t[language].title}</h1>
          
          <div className="flex items-center space-x-4">
            {/* WhatsApp Bell */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowWhatsAppModal(true);
                  fetchWhatsAppMessages();
                }}
                className="relative bg-white bg-opacity-20 p-2 rounded-full hover:bg-opacity-30 transition-colors"
                title={language === 'fr' ? 'Messages WhatsApp' : 'WhatsApp Messages'}
              >
                <span className="text-xl">💬</span>
                {whatsappUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                    {whatsappUnreadCount > 9 ? '9+' : whatsappUnreadCount}
                  </span>
                )}
              </button>
            </div>
            
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  if (!showNotifications) {
                    markNotificationsAsRead();
                  }
                }}
                className="relative bg-white bg-opacity-20 p-2 rounded-full hover:bg-opacity-30 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              
              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 bg-white rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h3 className="font-medium text-gray-900">{t[language].notifications}</h3>
                      {notifications.length > 0 && (
                        <button
                          onClick={markNotificationsAsRead}
                          className="text-sm text-green-600 hover:text-green-700"
                        >
                          {t[language].markAllRead}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.slice(0, 10).map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 border-b border-gray-100 hover:bg-gray-50 ${
                            !notification.read ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-start">
                            <div className="mr-3 text-lg">
                              {notification.type === 'testimony' ? '📝' : '📞'}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {notification.message}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(notification.timestamp).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        {t[language].noNotifications}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Language Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm">{t[language].language}:</span>
              <button
                onClick={toggleLanguage}
                className="bg-white bg-opacity-20 px-3 py-1 rounded text-sm font-medium hover:bg-opacity-30"
              >
                {language === 'en' ? '🇫🇷 Français' : '🇬🇧 English'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto">
          <div className="flex overflow-x-auto">
            {[
              { key: 'celebrations', label: t[language].celebrations },
              { key: 'weather', label: t[language].weather },
              { key: 'presenters', label: t[language].presenters },
              { key: 'social', label: t[language].social },
              { key: 'testimony', label: t[language].testimony },
              { key: 'calls', label: t[language].calls },
              { key: 'submissions', label: t[language].submissions }
            ].map((section) => (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 ${
                  activeSection === section.key
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-4">
        
        {/* Export Button */}
        <div className="mb-4 text-right">
          <button
            onClick={exportData}
            className="bg-green-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-green-700"
          >
            📤 {t[language].export}
          </button>
        </div>

        {/* Celebrations Section */}
        {activeSection === 'celebrations' && (
          <div className="space-y-4">
            {/* Now On Air Module */}
            {!nowOnAirLoading && nowOnAir && (
              <div className="bg-gradient-to-r from-kioo-primary to-kioo-accent text-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="animate-pulse mr-3">
                      <span className="text-3xl">📻</span>
                    </div>
                    <h2 className="text-2xl font-bold">{t[language].nowOnAir}</h2>
                  </div>
                  <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-1 rounded-full">
                    <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                    <span className="text-sm font-semibold">{t[language].live}</span>
                  </div>
                </div>
                
                {nowOnAir.current_program ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Current Program */}
                    <div className="bg-white bg-opacity-10 backdrop-blur rounded-lg p-4">
                      <div className="text-sm font-medium text-white text-opacity-80 mb-2">
                        {t[language].currentProgram}
                      </div>
                      <h3 className="text-xl font-bold mb-2">
                        {nowOnAir.current_program.title}
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <span className="mr-2">👤</span>
                          <span>{t[language].host}: {nowOnAir.current_program.host}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="mr-2">⏱️</span>
                          <span>{t[language].duration}: {nowOnAir.current_program.duration} {t[language].minutes}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="mr-2">🕐</span>
                          <span>{t[language].endTime}: {nowOnAir.current_program.end_time}</span>
                        </div>
                        {nowOnAir.current_program.language && (
                          <div className="flex items-center">
                            <span className="mr-2">🌐</span>
                            <span className="capitalize">{nowOnAir.current_program.language}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Next Program */}
                    {nowOnAir.next_program && (
                      <div className="bg-white bg-opacity-10 backdrop-blur rounded-lg p-4">
                        <div className="text-sm font-medium text-white text-opacity-80 mb-2">
                          {t[language].nextProgram}
                        </div>
                        <h3 className="text-xl font-bold mb-2">
                          {nowOnAir.next_program.title}
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center">
                            <span className="mr-2">👤</span>
                            <span>{t[language].host}: {nowOnAir.next_program.host}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="mr-2">🕐</span>
                            <span>{t[language].startTime}: {nowOnAir.next_program.start_time}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="mr-2">⏱️</span>
                            <span>{t[language].duration}: {nowOnAir.next_program.duration} {t[language].minutes}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-white bg-opacity-10 backdrop-blur rounded-lg">
                    <span className="text-4xl mb-2 block">🎵</span>
                    <p className="text-lg">{t[language].noCurrentProgram}</p>
                  </div>
                )}
                
                {/* Current Time Display */}
                <div className="mt-4 text-center text-sm text-white text-opacity-70">
                  {t[language].currentTime || 'Current Time'}: {nowOnAir.current_time} GMT | {nowOnAir.current_day?.toUpperCase()}
                </div>
              </div>
            )}
            
            <div className="bg-gradient-to-r from-green-50 to-kioo-light rounded-lg shadow p-6 border border-green-100">
              {/* Section Title */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-kioo-dark mb-2">
                  {language === 'fr' ? 'Célébrations d\'Aujourd\'hui' : 'Today\'s Celebrations'}
                </h2>
              </div>
              
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <span className="text-3xl mr-3">🎉</span>
                </div>
                
                {/* Language Toggle for Greetings */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCelebrationLanguage('en')}
                    className={`px-3 py-1 rounded text-sm font-medium transition ${
                      celebrationLanguage === 'en' 
                        ? 'bg-kioo-primary text-white' 
                        : 'bg-white text-gray-600 hover:bg-green-100'
                    }`}
                  >
                    🇬🇧 English
                  </button>
                  <button
                    onClick={() => setCelebrationLanguage('fr')}
                    className={`px-3 py-1 rounded text-sm font-medium transition ${
                      celebrationLanguage === 'fr' 
                        ? 'bg-kioo-primary text-white' 
                        : 'bg-white text-gray-600 hover:bg-green-100'
                    }`}
                  >
                    🇫🇷 Français
                  </button>
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">📻</span>
                  <p className="text-sm text-kioo-secondary">{t[language].readOnAir}</p>
                </div>
              </div>
              
              {celebrationsLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-kioo-primary"></div>
                  <p className="mt-2 text-gray-600">{t[language].celebrationsLoading}</p>
                </div>
              ) : celebrations.length > 0 ? (
                <div className="space-y-4">
                  {celebrations.map((celebration) => (
                    <div
                      key={celebration.id}
                      className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
                        celebration.celebration_type === 'birthday'
                          ? 'border-kioo-primary'
                          : 'border-kioo-accent'
                      } ${celebration.displayed ? 'opacity-60' : ''}`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">
                            {celebration.celebration_type === 'birthday' ? '🎂' : '💑'}
                          </span>
                          <div>
                            <h3 className="text-lg font-semibold text-kioo-dark">
                              {celebration.contact_name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {celebration.location}
                            </p>
                          </div>
                        </div>
                        
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          celebration.celebration_type === 'birthday'
                            ? 'bg-green-100 text-kioo-secondary'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}>
                          {celebration.celebration_type === 'birthday' 
                            ? t[language].birthdayCelebration 
                            : t[language].anniversaryCelebration}
                        </span>
                      </div>
                      
                      <div className="bg-gray-50 rounded-lg p-4 mb-3">
                        <p className="text-gray-800 leading-relaxed">
                          {celebrationLanguage === 'en' 
                            ? celebration.greeting_english 
                            : celebration.greeting_french}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        {!celebration.displayed && (
                          <button
                            onClick={() => markCelebrationDisplayed(celebration.id)}
                            className="w-full bg-kioo-secondary hover:bg-kioo-primary text-white font-medium py-2 px-4 rounded transition"
                          >
                            ✓ {t[language].markAsRead}
                          </button>
                        )}
                        
                        {celebration.displayed && (
                          <>
                            <button
                              onClick={() => copyToWhatsApp(celebration)}
                              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium py-2 px-4 rounded transition flex items-center justify-center gap-2"
                            >
                              <span>💬</span>
                              <span>{language === 'fr' ? 'Copier pour WhatsApp' : 'Copy to WhatsApp'}</span>
                            </button>
                            <div className="text-center text-xs text-gray-500">
                              {language === 'fr' ? 'Copiez et collez dans WhatsApp' : 'Copy and paste to WhatsApp'}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <span className="text-6xl mb-4 block">🎈</span>
                  <p className="text-gray-600">{t[language].noCelebrations}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Weather Section */}
        {activeSection === 'weather' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">{t[language].weather}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(weatherData).map(([city, data]) => (
                <div key={city} className="border rounded p-4">
                  <div className="font-medium text-gray-900">{city}</div>
                  <div className="text-2xl font-bold text-blue-600">{data.temperature}°C</div>
                  <div className="text-sm text-gray-600">{data.condition}</div>
                  <div className="text-xs text-gray-500 mt-2">
                    {t[language].updated}: {data.updated}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Makona Talk Show Section */}
        {activeSection === 'weather' && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg shadow p-6 mt-6">
            <div className="flex items-center mb-4">
              <div className="text-2xl mr-3">⭐</div>
              <h2 className="text-lg font-semibold text-yellow-800">Makona Talk Show - Special Program</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="mb-4">
                  <div className="text-sm font-medium text-yellow-700 mb-2">📅 Schedule</div>
                  <div className="text-lg font-bold text-yellow-900">Saturday • 6:00 AM - 9:00 AM</div>
                  <div className="text-sm text-yellow-700">3-hour live broadcast</div>
                </div>
                
                <div className="mb-4">
                  <div className="text-sm font-medium text-yellow-700 mb-2">🎙️ Format</div>
                  <div className="text-sm text-yellow-800">
                    ALL presenters from all three countries broadcasting LIVE together
                  </div>
                </div>
              </div>
              
              <div>
                <div className="text-sm font-medium text-yellow-700 mb-3">🌍 Countries Participating LIVE</div>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">🇱🇷</span>
                    <span className="bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      Liberia LIVE
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-lg mr-2">🇸🇱</span>
                    <span className="bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      Sierra Leone LIVE
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-lg mr-2">🇬🇳</span>
                    <span className="bg-green-200 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      Guinea LIVE
                    </span>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-yellow-100 rounded">
                  <div className="text-xs text-yellow-700">
                    <strong>Important:</strong> All presenters must be present in Foya for this special program
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Presenters by Country Section - Restored to Original */}
        {activeSection === 'presenters' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-6">{t[language].presenters}</h2>
            
            <div className="space-y-8">
              {Object.entries(presenters).map(([country, countryPresenters]) => (
                <div key={country} className="border rounded-lg p-4">
                  <h3 className="text-xl font-bold mb-4 text-green-600 capitalize flex items-center">
                    {country === 'liberia' && '🇱🇷'} 
                    {country === 'sierra_leone' && '🇸🇱'} 
                    {country === 'guinea' && '🇬🇳'} 
                    <span className="ml-2">
                      {country === 'liberia' ? 'Liberia' : ''}
                      {country === 'sierra_leone' ? 'Sierra Leone' : ''}
                      {country === 'guinea' ? 'Guinea' : ''}
                    </span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {countryPresenters.map((presenter, index) => (
                      <div key={index} className="bg-gray-50 rounded p-4">
                        <div className="font-semibold text-gray-900 mb-2">{presenter.name}</div>
                        
                        <div className="mb-3">
                          <div className="text-sm font-medium text-gray-600 mb-1">Programs:</div>
                          <div className="space-y-1">
                            {presenter.programs.map((program, programIndex) => (
                              <span key={programIndex} className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded mr-1 mb-1">
                                {program}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-sm font-medium text-gray-600 mb-1">Schedule:</div>
                          <div className="text-sm text-gray-800">{presenter.schedule}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {countryPresenters.length === 0 && (
                    <div className="text-center text-gray-500 py-8">
                      No presenters found for this country
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {Object.keys(presenters).length === 0 && (
              <div className="text-center text-gray-500 py-12">
                <div className="text-4xl mb-4">👥</div>
                <div>Loading presenters information...</div>
              </div>
            )}
          </div>
        )}

        {/* Social Media Section */}
        {activeSection === 'social' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">{t[language].social}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* WhatsApp */}
              <div className="border rounded p-4">
                <h3 className="font-medium mb-3">WhatsApp</h3>
                <a
                  href="https://web.whatsapp.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 text-white px-4 py-2 rounded text-sm hover:bg-green-600"
                >
                  💬 Open WhatsApp Web
                </a>
              </div>

              {/* Facebook */}
              <div className="border rounded p-4">
                <h3 className="font-medium mb-3">Facebook</h3>
                <a
                  href="http://facebook.com/kiooradio/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-blue-500 text-white px-4 py-2 rounded text-sm hover:bg-blue-600"
                >
                  📘 Visit Facebook Page
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Submissions Section */}
        {activeSection === 'submissions' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-6">{t[language].submissions}</h2>
            
            {/* Testimonies */}
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-4 text-green-600">📝 {t[language].testimonies}</h3>
              {submittedTestimonies.length > 0 ? (
                <div className="space-y-4">
                  {submittedTestimonies.slice(0, 5).map((testimony) => (
                    <div key={testimony.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium text-gray-900">
                          {testimony.name || 'Anonymous'} - {testimony.location}
                        </div>
                        <div className="text-xs text-gray-500">
                          {t[language].submittedOn} {new Date(testimony.submittedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>Program:</strong> {testimony.program}
                      </div>
                      <div className="text-sm text-gray-800">
                        {testimony.summary}
                      </div>
                    </div>
                  ))}
                  {submittedTestimonies.length > 5 && (
                    <div className="text-center text-sm text-gray-500">
                      ... and {submittedTestimonies.length - 5} more testimonies
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  {t[language].noSubmissions}
                </div>
              )}
            </div>

            {/* Call Logs */}
            <div>
              <h3 className="text-lg font-medium mb-4 text-blue-600">📞 {t[language].callLogs}</h3>
              {submittedCalls.length > 0 ? (
                <div className="space-y-4">
                  {submittedCalls.slice(0, 5).map((call) => (
                    <div key={call.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-medium text-gray-900">
                          {call.category} - {call.phone || 'No phone number'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {t[language].submittedOn} {new Date(call.submittedAt).toLocaleDateString()} at {call.time}
                        </div>
                      </div>
                      <div className="text-sm text-gray-800">
                        {call.summary}
                      </div>
                    </div>
                  ))}
                  {submittedCalls.length > 5 && (
                    <div className="text-center text-sm text-gray-500">
                      ... and {submittedCalls.length - 5} more call logs
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  {t[language].noSubmissions}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Testimony Form */}
        {activeSection === 'testimony' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">{t[language].testimony}</h2>
            <form onSubmit={handleTestimonySubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t[language].date}</label>
                  <input
                    type="date"
                    value={testimonyForm.date}
                    onChange={(e) => setTestimonyForm({...testimonyForm, date: e.target.value})}
                    className="w-full p-2 border rounded text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t[language].name}</label>
                  <input
                    type="text"
                    value={testimonyForm.name}
                    onChange={(e) => setTestimonyForm({...testimonyForm, name: e.target.value})}
                    className="w-full p-2 border rounded text-sm"
                    placeholder={t[language].name}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t[language].location}</label>
                  <input
                    type="text"
                    value={testimonyForm.location}
                    onChange={(e) => setTestimonyForm({...testimonyForm, location: e.target.value})}
                    className="w-full p-2 border rounded text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t[language].programName}</label>
                  <input
                    type="text"
                    value={testimonyForm.program}
                    onChange={(e) => setTestimonyForm({...testimonyForm, program: e.target.value})}
                    className="w-full p-2 border rounded text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t[language].summary}</label>
                <textarea
                  value={testimonyForm.summary}
                  onChange={(e) => setTestimonyForm({...testimonyForm, summary: e.target.value})}
                  className="w-full p-2 border rounded text-sm h-24"
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded font-medium hover:bg-green-700"
              >
                {t[language].submit}
              </button>
            </form>
          </div>
        )}

        {/* Call Log Form */}
        {activeSection === 'calls' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">{t[language].calls}</h2>
            <form onSubmit={handleCallSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t[language].date}</label>
                  <input
                    type="date"
                    value={callForm.date}
                    onChange={(e) => setCallForm({...callForm, date: e.target.value})}
                    className="w-full p-2 border rounded text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t[language].time}</label>
                  <input
                    type="time"
                    value={callForm.time}
                    onChange={(e) => setCallForm({...callForm, time: e.target.value})}
                    className="w-full p-2 border rounded text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t[language].phoneNumber}</label>
                  <input
                    type="tel"
                    value={callForm.phone}
                    onChange={(e) => setCallForm({...callForm, phone: e.target.value})}
                    className="w-full p-2 border rounded text-sm"
                    placeholder={t[language].phoneNumber}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t[language].category}</label>
                <select
                  value={callForm.category}
                  onChange={(e) => setCallForm({...callForm, category: e.target.value})}
                  className="w-full p-2 border rounded text-sm"
                  required
                >
                  {Object.entries(t[language].categories).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t[language].summary}</label>
                <textarea
                  value={callForm.summary}
                  onChange={(e) => setCallForm({...callForm, summary: e.target.value})}
                  className="w-full p-2 border rounded text-sm h-24"
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded font-medium hover:bg-green-700"
              >
                {t[language].submit}
              </button>
            </form>
          </div>
        )}
      </div>
      
      {/* WhatsApp Messages Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-kioo-primary text-white p-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-2xl">💬</span>
                <h2 className="text-xl font-bold">
                  {language === 'fr' ? 'Messages WhatsApp' : 'WhatsApp Messages'}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCalendarView(!showCalendarView)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-sm"
                >
                  📅 {language === 'fr' ? 'Archives' : 'Calendar'}
                </button>
                <button
                  onClick={fetchWhatsAppMessages}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-sm"
                >
                  🔄 {language === 'fr' ? 'Actualiser' : 'Refresh'}
                </button>
                <button
                  onClick={() => setShowWhatsAppModal(false)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-sm"
                >
                  ✕
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {!showCalendarView ? (
                /* Recent Messages View */
                whatsappLoading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-kioo-primary"></div>
                    <p className="mt-4 text-gray-600">{language === 'fr' ? 'Chargement...' : 'Loading...'}</p>
                  </div>
                ) : whatsappMessages.length > 0 ? (
                  <div className="space-y-4">
                    {whatsappMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`border-l-4 border-kioo-primary bg-green-50 p-4 rounded-lg ${
                          msg.read ? 'opacity-70' : ''
                        }`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-kioo-dark text-lg">{msg.sender}</h4>
                            <p className="text-sm text-gray-600">{msg.phone}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-gray-500">
                              {new Date(msg.received_at).toLocaleDateString()} {new Date(msg.received_at).toLocaleTimeString()}
                            </span>
                            {msg.is_testimony && (
                              <div className="mt-1">
                                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                                  🙏 {language === 'fr' ? 'Témoignage' : 'Testimony'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-gray-800 mb-4 leading-relaxed">{msg.message}</p>
                        
                        <div className="flex gap-2 flex-wrap">
                          {!msg.read && (
                            <button
                              onClick={async () => {
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                                await fetch(`${backendUrl}/api/whatsapp/messages/${msg.id}/mark-read`, {
                                  method: 'POST'
                                });
                                fetchWhatsAppMessages();
                                fetchWhatsAppUnreadCount();
                              }}
                              className="text-sm px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition"
                            >
                              ✓ {language === 'fr' ? 'Marquer lu' : 'Mark Read'}
                            </button>
                          )}
                          {!msg.responded && (
                            <button
                              onClick={async () => {
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                                await fetch(`${backendUrl}/api/whatsapp/messages/${msg.id}/mark-responded`, {
                                  method: 'POST'
                                });
                                fetchWhatsAppMessages();
                              }}
                              className="text-sm px-4 py-2 bg-kioo-primary hover:bg-kioo-secondary text-white rounded transition"
                            >
                              ✓ {language === 'fr' ? 'Répondu' : 'Responded'}
                            </button>
                          )}
                          {msg.is_testimony && msg.auto_saved_as_testimony && (
                            <span className="text-sm px-4 py-2 bg-purple-50 text-purple-700 rounded border border-purple-200">
                              {language === 'fr' ? 'Sauvegardé comme témoignage' : 'Saved as Testimony'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <span className="text-6xl mb-4 block">📱</span>
                    <p className="text-gray-600 text-lg">
                      {language === 'fr' ? 'Aucun message WhatsApp' : 'No WhatsApp messages'}
                    </p>
                  </div>
                )
              ) : (
                /* Calendar Archive View */
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-kioo-dark mb-2">
                      {language === 'fr' ? 'Archives par Date' : 'Message Archives'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {language === 'fr' ? 'Sélectionnez une date pour voir les messages' : 'Select a date to view messages'}
                    </p>
                  </div>
                  
                  {/* Simple Date Selector */}
                  <div className="mb-6">
                    <input
                      type="date"
                      onChange={(e) => {
                        if (e.target.value) {
                          fetchMessagesByDate(e.target.value);
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
                    />
                  </div>
                  
                  {/* Selected Date Messages */}
                  {selectedDate && (
                    <div>
                      <h4 className="font-semibold text-kioo-dark mb-4">
                        {language === 'fr' ? 'Messages du' : 'Messages from'} {new Date(selectedDate).toLocaleDateString()}
                      </h4>
                      
                      {calendarMessages.length > 0 ? (
                        <div className="space-y-3">
                          {calendarMessages.map((msg) => (
                            <div key={msg.id} className="border-l-4 border-kioo-primary bg-green-50 p-4 rounded">
                              <div className="flex justify-between mb-2">
                                <div>
                                  <h5 className="font-semibold">{msg.sender}</h5>
                                  <p className="text-sm text-gray-600">{msg.phone}</p>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {new Date(msg.received_at).toLocaleTimeString()}
                                </span>
                              </div>
                              <p className="text-gray-800">{msg.message}</p>
                              {msg.is_testimony && (
                                <span className="inline-block mt-2 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                                  🙏 {language === 'fr' ? 'Témoignage' : 'Testimony'}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-600 py-8">
                          {language === 'fr' ? 'Aucun message pour cette date' : 'No messages for this date'}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresentersDashboard;